/**
 * Bazaarvoice injects review structured data that Google cannot validate on its own:
 * - `#bv-jsonld-bvloader-summary` / `#bv-jsonld-reviews-data` use an `@id` taken from the canonical
 *   link or, when missing, the full URL including query params, so it may not match the PDP Product.
 * - Reviews in `#bv-jsonld-reviews-data` have no `@type` or `itemReviewed`.
 * - `inline_rating` widgets (product tiles) render `AggregateRating` microdata with no parent
 *   item, which Google reports as "missing itemReviewed" and competes with the PDP rating.
 * These helpers fix that output in place so the page only exposes ratings linked to the PDP Product.
 */

export const PRODUCT_JSONLD_ID = 'pdp-product-jsonld'
export const BV_JSONLD_SELECTOR = 'script#bv-jsonld-bvloader-summary, script#bv-jsonld-reviews-data'
export const BV_INLINE_RATING_MICRODATA_SELECTOR = [
    '[data-bv-show="inline_rating"] [itemscope]',
    '[data-bv-show="inline_rating"] [itemprop]',
    '[data-bv-show="inline_rating"] [itemtype]'
].join(',')

const parseJson = (text) => {
    try {
        return JSON.parse(text)
    } catch {
        return null
    }
}

const getProductSchemaId = (root) =>
    parseJson(root.getElementById?.(PRODUCT_JSONLD_ID)?.textContent)?.['@id']

/**
 * @param {Object} data - Parsed Bazaarvoice JSON-LD (summary or reviews).
 * @param {string} [productId] - `@id` of the PDP Product node. Defaults to the Bazaarvoice `@id`.
 * @returns {Object|null} Normalized data, or null when nothing needs to change.
 */
export const normalizeBvJsonLd = (data, productId = data?.['@id']) => {
    if (!data || !productId) return null

    const reviews = Array.isArray(data.review) ? data.review : null
    const hasMatchingId = data['@id'] === productId
    const hasLinkedReviews =
        !reviews ||
        reviews.every(
            (review) =>
                review?.['@type'] === 'Review' && review?.itemReviewed?.['@id'] === productId
        )
    if (hasMatchingId && hasLinkedReviews) return null

    return {
        ...data,
        '@id': productId,
        ...(reviews && {
            review: reviews.map((review) => ({
                ...review,
                '@type': 'Review',
                itemReviewed: {'@id': productId}
            }))
        })
    }
}

export const fixBvJsonLd = (root = document) => {
    const productId = getProductSchemaId(root)
    root.querySelectorAll(BV_JSONLD_SELECTOR).forEach((script) => {
        const normalized = normalizeBvJsonLd(parseJson(script.textContent), productId)
        if (normalized) {
            script.textContent = JSON.stringify(normalized)
        }
    })
}

export const removeBvInlineRatingMicrodata = (root = document) => {
    root.querySelectorAll(BV_INLINE_RATING_MICRODATA_SELECTOR).forEach((element) => {
        element.removeAttribute('itemscope')
        element.removeAttribute('itemtype')
        element.removeAttribute('itemprop')
    })
}
